import AddIcon from "@mui/icons-material/Add";
import {
	Autocomplete,
	Box,
	Button,
	ClickAwayListener,
	FormControlLabel,
	Menu,
	MenuItem,
	Stack,
	Switch,
	TextField,
} from "@mui/material";
import { ContactType, ProjectStatus, ProjectType } from "@repo/server-api";
import { type IdWithName } from "@repo/shared/common";
import { useAsyncSelectContactQuery } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useState } from "react";

const ALL_PROJECT_STATUSES = [
	ProjectStatus.Draft,
	ProjectStatus.OfferCreated,
	ProjectStatus.OfferSent,
	ProjectStatus.Active,
	ProjectStatus.Completed,
	ProjectStatus.Rejected,
	ProjectStatus.Canceled,
];

export type ProjectListToolbarProps = {
	searchValue: string;
	onSearchChange: (value: string) => void;
	showInactive: boolean;
	onShowInactiveChange: (value: boolean) => void;
	onAddProject: (type: ProjectType) => void;
	selectedClient: IdWithName | null;
	onClientChange: (client: IdWithName | null) => void;
	selectedStatuses: ProjectStatus[];
	onStatusChange: (statuses: ProjectStatus[]) => void;
};

export const ProjectListToolbar = ({
	searchValue,
	onSearchChange,
	showInactive,
	onShowInactiveChange,
	onAddProject,
	selectedClient,
	onClientChange,
	selectedStatuses,
	onStatusChange,
}: ProjectListToolbarProps) => {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	// Client search hook
	const clientSearchHook = useAsyncSelectContactQuery([ContactType.Client]);

	return (
		<Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexShrink: 0 }}>
			<TextField
				value={searchValue}
				onChange={(e) => onSearchChange(e.target.value)}
				placeholder={t("placeholder.search")}
				variant="outlined"
				sx={{ flex: 1 }}
				data-testid="project-search-input"
			/>
			<Autocomplete
				value={selectedClient}
				onChange={(_, newValue) => onClientChange(newValue)}
				options={clientSearchHook.items}
				getOptionLabel={(option) => option.name}
				isOptionEqualToValue={(option, value) => option.id === value.id}
				loading={clientSearchHook.loading}
				onInputChange={(_, value) => clientSearchHook.setSearchText(value)}
				onOpen={() => clientSearchHook.resetSearch()}
				filterOptions={(x) => x}
				renderInput={(params) => (
					<TextField
						{...params}
						label={t("client")}
						variant="outlined"
						size="small"
						data-testid="client-filter-input"
					/>
				)}
				sx={{ minWidth: 200 }}
				data-testid="client-filter-autocomplete"
			/>
			<Autocomplete
				multiple
				value={selectedStatuses}
				onChange={(_, newValue) => onStatusChange(newValue)}
				options={ALL_PROJECT_STATUSES}
				getOptionLabel={(status) => t(`project-status.${status.toLowerCase()}`)}
				isOptionEqualToValue={(a, b) => a === b}
				disableCloseOnSelect
				renderInput={(params) => (
					<TextField
						{...params}
						label={t("status")}
						variant="outlined"
						size="small"
						data-testid="status-filter-input"
					/>
				)}
				sx={{ minWidth: 200 }}
				data-testid="status-filter-autocomplete"
			/>
			<FormControlLabel
				control={
					<Switch
						checked={showInactive}
						onChange={(e) => onShowInactiveChange(e.target.checked)}
						data-testid="show-inactive-toggle"
					/>
				}
				label={t("show-inactive")}
			/>
			<ClickAwayListener onClickAway={() => setAnchorEl(null)}>
				<Box>
					<Button
						className="blueButton"
						color="secondary"
						onClick={(e) => setAnchorEl(e.currentTarget)}
						data-testid="add-project-button"
					>
						{t("add").toLocaleUpperCase()} <AddIcon />
					</Button>
					<Menu
						disablePortal
						open={Boolean(anchorEl)}
						anchorEl={anchorEl}
						onClose={() => setAnchorEl(null)}
					>
						<MenuItem>
							<Button
								className="textButton"
								variant="text"
								onClick={() => {
									onAddProject(ProjectType.Project);
									setAnchorEl(null);
								}}
								data-testid="create-project-button"
							>
								<AddIcon /> {t("create.project")}
							</Button>
						</MenuItem>
						<MenuItem>
							<Button
								className="textButton"
								variant="text"
								onClick={() => {
									onAddProject(ProjectType.Tender);
									setAnchorEl(null);
								}}
								data-testid="create-tender-button"
							>
								<AddIcon /> {t("create.tender")}
							</Button>
						</MenuItem>
					</Menu>
				</Box>
			</ClickAwayListener>
		</Stack>
	);
};
